import type {
  ClientConfig,
  TrelloApiResponse,
  TrelloCard,
  TrelloCustomFieldDefinition,
  TrelloList,
} from '@/types'

/**
 * Fetches all cards from a client's "Cronograma" list via the serverless proxy.
 * Never exposes Trello credentials to the browser.
 */
export async function fetchClientCards(client: ClientConfig): Promise<{
  cards: TrelloCard[]
  listFound: boolean
  error?: string
}> {
  const params = new URLSearchParams({
    boardId: client.boardId,
    listName: client.cronogramaListName,
  })

  const response = await fetch(`/api/trello?${params.toString()}`, {
    headers: { 'Content-Type': 'application/json' },
  })

  if (!response.ok) {
    let message = `HTTP ${response.status}`
    try {
      const body = (await response.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // ignore JSON parse error
    }
    return { cards: [], listFound: false, error: message }
  }

  const data = (await response.json()) as TrelloApiResponse
  return data
}

/**
 * Fetches ALL cards from a board (bypasses list filter), plus the board's
 * lists and Custom Field definitions.
 * Used by the Operação board to calculate outsourced-production demands/costs.
 */
export async function fetchBoardAllCards(boardId: string): Promise<{
  cards: TrelloCard[]
  lists: TrelloList[]
  customFields: TrelloCustomFieldDefinition[]
  error?: string
}> {
  const params = new URLSearchParams({ boardId, allCards: 'true' })

  const response = await fetch(`/api/trello?${params.toString()}`, {
    headers: { 'Content-Type': 'application/json' },
  })

  if (!response.ok) {
    let message = `HTTP ${response.status}`
    try {
      const body = (await response.json()) as { error?: string }
      if (body.error) message = body.error
    } catch {
      // ignore JSON parse error
    }
    return { cards: [], lists: [], customFields: [], error: message }
  }

  const data = (await response.json()) as TrelloApiResponse
  if (data.error) return { cards: [], lists: [], customFields: [], error: data.error }
  return { cards: data.cards, lists: data.lists ?? [], customFields: data.customFields ?? [] }
}

/**
 * Fetches cards for all clients in parallel.
 */
export async function fetchAllClientsCards(
  clients: ClientConfig[]
): Promise<Map<string, { cards: TrelloCard[]; listFound: boolean; error?: string }>> {
  const results = await Promise.allSettled(
    clients.map((client) => fetchClientCards(client))
  )

  const map = new Map<string, { cards: TrelloCard[]; listFound: boolean; error?: string }>()

  results.forEach((result, index) => {
    const client = clients[index]
    if (result.status === 'fulfilled') {
      map.set(client.boardId, result.value)
    } else {
      const message =
        result.reason instanceof Error ? result.reason.message : 'Erro desconhecido'
      map.set(client.boardId, { cards: [], listFound: false, error: message })
    }
  })

  return map
}
